import { c as _c } from "react/compiler-runtime";
import { Alert, Button, Heading, HStack, Icon, List, Separator, Skeleton, Stack, Text } from '@chakra-ui/react';
import { LuArrowRight, LuCheck, LuUserMinus } from 'react-icons/lu';
import { membershipTermReadable } from './offers';
import { droppedCouponsLabel, formatPlanChangeDate, KEEP_CURRENT_PLAN_LABEL, keepPlanLabel, termChangeDisclosure, type PlanChangeLine } from './planChange';
export type PlanDowngradeSeat = {
  id: string;
  /** Advisor name, as My Advisors shows it. */
  name: string;
  /** Seat role, e.g. "Leadership". */
  role?: string | null;
};
export type PlanDowngradeReviewProps = {
  currentPlanName: string;
  currentProductId?: string | null;
  targetPlanName: string;
  /** Billing period the new plan renews on, e.g. "Monthly". */
  termLabel?: string | null;
  /**
   * L57: the same plan on a shorter billing term (yearly → monthly). The copy names the term,
   * says nothing is charged or credited now, and the cancel action is "Keep current plan".
   */
  termOnly?: boolean;
  /** Target term id for the term-change copy (`month`, `threeMonth`, …). */
  targetTermId?: string | null;
  /** When the change applies (the quote's `effectiveAt`). */
  effectiveAt?: string | null;
  /** Renewal price after the change, coupons applied (quote's next invoice estimate). */
  renewalPrice?: string | null;
  /** Coupons that apply only to the current plan and stop at the change. */
  droppedCouponIds?: Array<string | null> | null;
  /** Team advisor seats that end at the change, listed by advisor (L29). */
  endingSeats?: PlanDowngradeSeat[];
  seatsLoading?: boolean;
  seatsError?: string | null;
  /** Plan lines that end or shrink (`planChangeEndingLines`). */
  endingLines: PlanChangeLine[];
  quoteLoading?: boolean;
  quoteError?: string | null;
  onRetryQuote?: () => void;
  /** Scheduling in flight. */
  loading?: boolean;
  error?: string | null;
  onConfirm?: () => void;
  onCancel?: () => void;
  /** `plain` drops the panel chrome when the caller (a dialog) is already the surface. */
  variant?: 'card' | 'plain';
};

/**
 * Review for a downgrade at the end of the term: Team → Pro (W4) or a shorter billing term
 * (L57, `termOnly`). States the date, the real renewal price, what the member keeps until then
 * and what ends at the change, before anything is scheduled. Nothing is charged, credited or
 * refunded; the caller owns `scheduleSubscriptionDowngrade`.
 */
export const PlanDowngradeReview = t0 => {
  const $ = _c(77);
  const {
    currentPlanName,
    currentProductId,
    droppedCouponIds,
    effectiveAt,
    endingLines,
    endingSeats: t1,
    error,
    loading,
    onCancel,
    onConfirm,
    onRetryQuote,
    quoteError,
    quoteLoading,
    renewalPrice,
    seatsError,
    seatsLoading,
    targetPlanName,
    targetTermId,
    termLabel,
    termOnly: t2,
    variant: t3
  } = t0;
  let t4;
  if ($[0] !== t1) {
    t4 = t1 === undefined ? [] : t1;
    $[0] = t1;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const endingSeats = t4;
  const termOnly = t2 === undefined ? false : t2;
  const variant = t3 === undefined ? "card" : t3;
  let t5;
  if ($[2] !== effectiveAt) {
    t5 = formatPlanChangeDate(effectiveAt);
    $[2] = effectiveAt;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  const date = t5;
  const when = date ? `on ${date}` : "at your renewal";
  const until = date ? `until ${date}` : "until your renewal";
  let t6;
  if ($[4] !== droppedCouponIds) {
    t6 = droppedCouponsLabel(droppedCouponIds);
    $[4] = droppedCouponIds;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  const coupons = t6;
  const quoteReady = !quoteLoading && !quoteError;
  const endingCount = endingSeats.length + endingLines.length;
  const showEnding = endingCount > 0 || Boolean(seatsLoading) || Boolean(seatsError);
  let t7;
  if ($[6] !== targetTermId || $[7] !== termLabel) {
    t7 = targetTermId ? membershipTermReadable(targetTermId) : termLabel?.toLowerCase() || null;
    $[6] = targetTermId;
    $[7] = termLabel;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  const termName = t7;
  const target = termOnly && termName ? termName : targetPlanName;
  let t8;
  if ($[9] !== variant) {
    t8 = variant === "card" ? {
      bg: "colorPalette.panel",
      borderColor: "border",
      borderRadius: "lg",
      borderWidth: "1px",
      maxW: "30rem",
      p: {
        base: 5,
        md: 6
      }
    } : {};
    $[9] = variant;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = {
      base: "lg",
      md: "xl"
    };
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  let t10;
  if ($[12] !== target) {
    t10 = <Heading size={t9} textWrap="balance">Switch to {target} at renewal</Heading>;
    $[12] = target;
    $[13] = t10;
  } else {
    t10 = $[13];
  }
  let t11;
  if ($[14] !== currentPlanName || $[15] !== effectiveAt || $[16] !== targetTermId || $[17] !== termOnly || $[18] !== until) {
    t11 = termOnly ? termChangeDisclosure(targetTermId, effectiveAt) : `You keep ${currentPlanName} ${until}. Nothing is charged today and nothing is refunded.`;
    $[14] = currentPlanName;
    $[15] = effectiveAt;
    $[16] = targetTermId;
    $[17] = termOnly;
    $[18] = until;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  if ($[20] !== t11) {
    t12 = <Text color="fg.muted" textStyle="sm">{t11}</Text>;
    $[20] = t11;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  let t13;
  if ($[22] !== t10 || $[23] !== t12) {
    t13 = <Stack gap={1.5}>{t10}{t12}</Stack>;
    $[22] = t10;
    $[23] = t12;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  let t14;
  if ($[25] !== coupons || $[26] !== currentPlanName || $[27] !== onRetryQuote || $[28] !== quoteError || $[29] !== quoteLoading || $[30] !== renewalPrice || $[31] !== targetPlanName || $[32] !== termLabel || $[33] !== when) {
    t14 = <Stack bg="colorPalette.container" borderRadius="md" gap={2} px={4} py={3}>{quoteLoading ? <Stack aria-label="Calculating your renewal price" gap={2}><Skeleton h={5} w="60%" /><Skeleton h={4} w="80%" /></Stack> : quoteError ? <Alert.Root status="warning"><Alert.Indicator /><Stack align="start" gap={2}><Text textStyle="sm">{quoteError}</Text>{onRetryQuote ? <Button size="xs" variant="outline" onClick={onRetryQuote}>Try again</Button> : null}</Stack></Alert.Root> : <><HStack align="baseline" justify="space-between"><Text color="fg.muted" textStyle="xs">Changes {when}</Text><Text color="fg.muted" textStyle="xs">Due today $0</Text></HStack><HStack align="baseline" justify="space-between" wrap="wrap"><Text fontWeight="medium" textStyle="sm">Renews as {targetPlanName}{termLabel ? ` · ${termLabel}` : ""}</Text>{renewalPrice ? <Text fontVariantNumeric="tabular-nums" fontWeight="bold" textStyle="xl">{renewalPrice}</Text> : null}</HStack>{coupons ? <Text color="fg.muted" textStyle="xs">{`Your ${coupons} discount applies only to ${currentPlanName}, so it ends at the change. The price above is without it.`}</Text> : null}</>}</Stack>;
    $[25] = coupons;
    $[26] = currentPlanName;
    $[27] = onRetryQuote;
    $[28] = quoteError;
    $[29] = quoteLoading;
    $[30] = renewalPrice;
    $[31] = targetPlanName;
    $[32] = termLabel;
    $[33] = when;
    $[34] = t14;
  } else {
    t14 = $[34];
  }
  let t15;
  if ($[35] !== until) {
    t15 = <Text fontWeight="medium" textStyle="sm">You keep {until}</Text>;
    $[35] = until;
    $[36] = t15;
  } else {
    t15 = $[36];
  }
  let t16;
  if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <Icon aria-hidden={true} color="fg.success" mt={0.5}><LuCheck /></Icon>;
    $[37] = t16;
  } else {
    t16 = $[37];
  }
  const t17 = termOnly ? `Everything in ${currentPlanName}. Only the billing period changes.` : showEnding ? `Everything in ${currentPlanName}, including the items below.` : `Everything in ${currentPlanName}.`;
  let t18;
  if ($[38] !== t17) {
    t18 = <HStack align="start" gap={2}>{t16}<Text textStyle="sm">{t17}</Text></HStack>;
    $[38] = t17;
    $[39] = t18;
  } else {
    t18 = $[39];
  }
  let t19;
  if ($[40] !== t15 || $[41] !== t18) {
    t19 = <Stack gap={2}>{t15}{t18}</Stack>;
    $[40] = t15;
    $[41] = t18;
    $[42] = t19;
  } else {
    t19 = $[42];
  }
  let t20;
  if ($[43] !== endingCount || $[44] !== endingLines || $[45] !== endingSeats || $[46] !== seatsError || $[47] !== seatsLoading || $[48] !== showEnding || $[49] !== when) {
    t20 = showEnding ? <><Separator /><Stack gap={2}><Text fontWeight="medium" textStyle="sm">Ends {when}</Text>{seatsLoading ? <Stack aria-label="Loading your advisor seats" gap={2}><Skeleton h={4} w="70%" /><Skeleton h={4} w="55%" /></Stack> : seatsError ? <Text color="fg.muted" textStyle="xs">{seatsError}</Text> : null}{endingCount > 0 ? <List.Root gap={2} variant="plain">{endingSeats.map(_temp)}{endingLines.map(_temp2)}</List.Root> : null}</Stack></> : null;
    $[43] = endingCount;
    $[44] = endingLines;
    $[45] = endingSeats;
    $[46] = seatsError;
    $[47] = seatsLoading;
    $[48] = showEnding;
    $[49] = when;
    $[50] = t20;
  } else {
    t20 = $[50];
  }
  let t21;
  if ($[51] !== error) {
    t21 = error ? <Alert.Root status="error"><Alert.Indicator /><Text>{error}</Text></Alert.Root> : null;
    $[51] = error;
    $[52] = t21;
  } else {
    t21 = $[52];
  }
  let t22;
  if ($[53] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = {
      base: "column",
      sm: "row"
    };
    $[53] = t22;
  } else {
    t22 = $[53];
  }
  const t23 = !quoteReady || loading || !onConfirm;
  const t24 = date ? `on ${date}` : "at renewal";
  let t25;
  if ($[54] !== loading || $[55] !== onConfirm || $[56] !== t23 || $[57] !== t24 || $[58] !== target) {
    t25 = <Button disabled={t23} flex={1} loading={loading} variant="cta" onClick={onConfirm}>Switch to {target} {t24}</Button>;
    $[54] = loading;
    $[55] = onConfirm;
    $[56] = t23;
    $[57] = t24;
    $[58] = target;
    $[59] = t25;
  } else {
    t25 = $[59];
  }
  let t26;
  if ($[60] !== currentPlanName || $[61] !== currentProductId || $[62] !== loading || $[63] !== onCancel || $[64] !== termOnly) {
    t26 = onCancel ? <Button disabled={loading} flex={1} variant="outline" onClick={onCancel}>{termOnly ? KEEP_CURRENT_PLAN_LABEL : keepPlanLabel(currentProductId, currentPlanName)}</Button> : null;
    $[60] = currentPlanName;
    $[61] = currentProductId;
    $[62] = loading;
    $[63] = onCancel;
    $[64] = termOnly;
    $[65] = t26;
  } else {
    t26 = $[65];
  }
  let t27;
  if ($[66] !== t25 || $[67] !== t26) {
    t27 = <Stack direction={t22} gap={3}>{t25}{t26}</Stack>;
    $[66] = t25;
    $[67] = t26;
    $[68] = t27;
  } else {
    t27 = $[68];
  }
  let t28;
  if ($[69] !== t13 || $[70] !== t14 || $[71] !== t19 || $[72] !== t20 || $[73] !== t21 || $[74] !== t27 || $[75] !== t8) {
    t28 = <Stack gap={5} w="full" {...t8}>{t13}{t14}{t19}{t20}{t21}{t27}</Stack>;
    $[69] = t13;
    $[70] = t14;
    $[71] = t19;
    $[72] = t20;
    $[73] = t21;
    $[74] = t27;
    $[75] = t8;
    $[76] = t28;
  } else {
    t28 = $[76];
  }
  return t28;
};
function _temp(seat) {
  return <List.Item key={seat.id} alignItems="start" textStyle="sm"><List.Indicator asChild={true} color="fg.muted" mt={0.5}><LuUserMinus /></List.Indicator><Text>Advisor seat:{" "}<Text as="span" fontWeight="medium">{seat.name}</Text>{seat.role ? ` (${seat.role})` : ""}</Text></List.Item>;
}
function _temp2(line) {
  return <List.Item key={line.slug} alignItems="start" textStyle="sm"><List.Indicator asChild={true} color="fg.muted" mt={0.5}><line.icon /></List.Indicator>{line.after ? <HStack gap={1.5} wrap="wrap"><Text>{line.title}:</Text><Text fontVariantNumeric="tabular-nums">{line.before}</Text><Icon aria-label="becomes" boxSize={3.5} color="fg.muted"><LuArrowRight /></Icon><Text fontVariantNumeric="tabular-nums" fontWeight="medium">{line.after}</Text></HStack> : <Text>{line.title}</Text>}</List.Item>;
}